import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useShift } from '@/hooks/useShift';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProgressRing } from '@/components/ui/progress-ring';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AudioRecorderButton } from '@/components/audio/AudioRecorderButton';

import { Pill, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

export default function HandoffPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { activeShift, refreshShift } = useShift();
  

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [textSummary, setTextSummary] = useState('');
  const [stats, setStats] = useState({
    tasksCompleted: 0,
    tasksTotal: 0,
    sporadicMeds: 0,
    incidents: 0
  });


  const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  useEffect(() => {
    if (patientId && activeShift) {
      fetchStats();
    }
  }, [patientId, activeShift]);

  const fetchStats = async () => {
    if (!patientId || !activeShift) return;

    try {
      // Get executions for this shift
      const { data: executions } = await supabase
        .from('task_executions')
        .select('*')
        .eq('shift_id', activeShift.id);

      const incidentsCount = executions?.filter(e => e.adhoc_task_type === 'incident').length || 0;
      const sporadicMedsCount = executions?.filter(e =>
        e.adhoc_task_type === 'sporadic_medication' ||
        (e.task_id && e.is_sporadic)
      ).length || 0;

      // Get task instances for THIS shift
      const { data: instances } = await supabase
        .from('shift_task_instances')
        .select('status')
        .eq('shift_id', activeShift.id);

      const totalTasks = instances?.length || 0;
      const completedTasks = instances?.filter(i => i.status === 'completed').length || 0;

      setStats({
        tasksCompleted: completedTasks,
        tasksTotal: totalTasks,
        sporadicMeds: sporadicMedsCount,
        incidents: incidentsCount
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate AI summary in background for family members (not shown to caregiver)
  const generateAISummaryForFamily = async (shiftId: string) => {
    try {
      await supabase.functions.invoke('generate-shift-summary', {
        body: { shiftId, patientId }
      });
    } catch (error) {
      console.error('Error generating AI summary for family:', error);
      // Silent fail - this is background processing
    }
  };

  const handleConfirmHandoff = async () => {
    if (!activeShift || !user || !patientId) return;

    if (!textSummary.trim()) {
      toast.error(t('handoff.summaryRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Create handoff record
      const { error: handoffError } = await supabase
        .from('handoffs')
        .insert({
          shift_id: activeShift.id,
          patient_id: patientId,
          from_caregiver_id: user.id,
          text_summary: textSummary.trim(),
          tasks_completed: stats.tasksCompleted,
          tasks_total: stats.tasksTotal,
          sporadic_meds_given: stats.sporadicMeds,
          incidents_count: stats.incidents,
          confirmed_at: new Date().toISOString()
        });

      if (handoffError) throw handoffError;

      // Complete the shift
      const { error: shiftError } = await supabase
        .from('shifts')
        .update({
          check_out_at: new Date().toISOString(),
          status: 'completed',
          notes: textSummary.trim()
        })
        .eq('id', activeShift.id);

      if (shiftError) throw shiftError;

      // Generate AI summary in background for family members (async, don't wait)
      generateAISummaryForFamily(activeShift.id);

      // Refresh the shift context
      await refreshShift();

      toast.success(t('handoff.shiftCompleted'));
      navigate('/app', { replace: true });
    } catch (error: any) {
      console.error('Error completing handoff:', error);
      toast.error(error.message || t('handoff.errorCompleting'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const adherencePercent = stats.tasksTotal > 0
    ? Math.round((stats.tasksCompleted / stats.tasksTotal) * 100)
    : 100;

  // Calculate shift duration
  const getShiftDuration = () => {
    if (!activeShift) return '';
    const checkInTime = parseISO(activeShift.check_in_at);
    const now = new Date();
    const totalMinutes = differenceInMinutes(now, checkInTime);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  if (isLoading || !activeShift) {
    return (
      <AppLayout
        title={t('handoff.title')}
        showBack
        onBack={() => navigate(`/app/caregiver/${patientId}`)}
      >
        <div className="p-4 animate-pulse space-y-4">
          <div className="h-16 bg-muted rounded" />
          <div className="h-32 bg-muted rounded" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t('handoff.title')}
      showBack
      onBack={() => navigate(`/app/caregiver/${patientId}`)}
    >
      <div className="p-4">
        {/* Subheader with shift info */}
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {t('handoff.checkInAt')}: {format(parseISO(activeShift.check_in_at), "HH:mm", { locale: dateLocale })}
                </span>
              </div>
              <div className="text-sm font-medium text-primary">
                {t('handoff.duration')}: {getShiftDuration()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards - Aligned with CaregiverAgenda */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="text-center p-4">
            <ProgressRing progress={adherencePercent} size={60} color="success">
              <span className="text-sm font-bold">{adherencePercent}%</span>
            </ProgressRing>
            <p className="text-xs text-muted-foreground mt-2">{t('dashboard.completion')}</p>
          </Card>

          <Card className="text-center p-4">
            <div className="flex flex-col items-center justify-center gap-1 mb-1">
              <div className="text-2xl font-bold text-primary">
                {stats.tasksCompleted} / {stats.tasksTotal}
              </div>
              <div className="flex gap-2 text-[12px] text-muted-foreground">
                <span>{t('dashboard.executedTasks')}: {stats.tasksCompleted}</span>
                <span>•</span>
                <span>{t('dashboard.plannedTasks')}: {stats.tasksTotal}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('handoff.tasksCompleted')}</p>
          </Card>

          <Card className="text-center p-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Pill className="w-6 h-6 text-sporadic" />
              <span className="text-2xl font-bold">{stats.sporadicMeds}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('dashboard.sporadicMeds')}</p>
          </Card>

          <Card className="text-center p-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="w-6 h-6 text-destructive" />
              <span className="text-2xl font-bold">{stats.incidents}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('dashboard.incidents')}</p>
          </Card>
        </div>

        {/* Manual Summary - Important for caregiver to write detailed notes */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('handoff.shiftSummary')} *</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {t('handoff.summaryImportanceNote')}
            </p>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder={t('handoff.summaryPlaceholder')}
              value={textSummary}
              onChange={(e) => setTextSummary(e.target.value)}
              rows={6}
              className="mb-3 max-h-48 overflow-y-auto resize-none"
            />

            {/* Audio recording - only shown on mobile native app */}
            <AudioRecorderButton
              onTranscriptionComplete={(text) => {
                // Replace text completely with transcription
                setTextSummary(text);
              }}
              onDiscard={() => {
                // Clear text when audio is discarded
                setTextSummary('');
              }}
              disabled={isSubmitting}
            />
          </CardContent>
        </Card>

        {/* Confirmation button */}
        <Card>
          <CardContent className="py-4">
            <Button
              className="w-full"
              size="lg"
              onClick={handleConfirmHandoff}
              disabled={!textSummary.trim() || isSubmitting}
            >
              {isSubmitting ? t('common.loading') : t('handoff.finishShift')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
